import { Stack } from '@mui/material'
import { CanceledError } from 'axios'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { LoadingButton } from 'components/common/LoadingButton'
import { TestEvaluationResultsStatusCheckIntegration } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { useValidateEvaluationConfiguration } from 'components/status-checks/hooks/use-validate-evaluation-configuration'
import {
  buildEvaluationConfigurationFromFormValues,
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import toast from 'lib/toast'
import {
  type EndpointConfiguration,
  type EndpointResponse
} from 'types/api/status-checks/status-check'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { EvaluationResults } from 'types/api/status-checks/evaluation'

/**
 * Action built to display Custom Status Check Evaluation results (exclusively). All other
 * observability tool types use `TestStatusCheck` for both connection and evaluation testing.
 */
export const TestStatusCheckSuccessEvaluation: React.FC<{
  disabled?: boolean
  endpointConfiguration: EndpointConfiguration | undefined
  endpointResponse: EndpointResponse | undefined
  onChanged: (isEvaluationChecked: boolean) => void
}> = ({
  disabled = false,
  endpointConfiguration,
  endpointResponse,
  onChanged
}) => {
  const [abortController, setAbortController] = useState(new AbortController())
  const { aborted: isAborted } = abortController.signal

  const [isCanceling, setIsCanceling] = useState(false)
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResults>()

  const { getValues } = useFormContext<StatusCheckFormValues>()
  const { isStatusCheckEvaluationValidated } = useStatusCheckDetailsContext()
  const {
    isLoading,
    reset: resetEvaluationConfiguration,
    validateEvaluationConfiguration
  } = useValidateEvaluationConfiguration(abortController)

  const testStatusCheckEvaluation = async () => {
    if (!endpointResponse) return

    resetEvaluationConfiguration()

    try {
      const fieldValues = getValues()
      const { testEvaluationResults } = await validateEvaluationConfiguration({
        endpointConfiguration,
        endpointResponse,
        evaluationConfiguration:
          buildEvaluationConfigurationFromFormValues(fieldValues),
        integration: buildTeamExternalIntegration(fieldValues)
      })

      setEvaluationResults(testEvaluationResults)
      onChanged(true)
    } catch (error) {
      onChanged(false)

      // Don't show an error toast if the request was cancelled.
      if (error instanceof CanceledError) return

      toast({
        type: 'error',
        text: getMessageFromResponseOrError(
          error,
          'There was an error while testing this evaluation. Please try again later.'
        )
      })
    } finally {
      setIsCanceling(false)
    }
  }

  // Blow away the evaluation results if the validation flag is false.
  useEffect(() => {
    setEvaluationResults((results) =>
      isStatusCheckEvaluationValidated ? results : undefined
    )
  }, [isStatusCheckEvaluationValidated])

  // Reinitializes the abort controller if the previous request was aborted.
  useEffect(() => {
    setAbortController((controller) =>
      isAborted ? new AbortController() : controller
    )
  }, [isAborted])

  return (
    <Stack>
      <Stack direction="row" sx={{ columnGap: 2, mb: 2 }}>
        <LoadingButton
          color="primary"
          data-testid="test-evaluation-status-check-integration-button"
          isDisabled={isLoading || disabled}
          isLoading={isLoading}
          size="small"
          variant="contained"
          onClick={testStatusCheckEvaluation}
        >
          {isLoading ? 'Testing Evaluation...' : 'Test Evaluation'}
        </LoadingButton>
        <LoadingButton
          color="secondary"
          data-testid="cancel-evaluation-status-check-integration-button"
          isDisabled={!isLoading || isCanceling}
          isLoading={isCanceling}
          size="small"
          variant="contained"
          onClick={() => {
            abortController.abort()
            setIsCanceling(true)
            setEvaluationResults(undefined)
            onChanged(false)
          }}
        >
          {isCanceling ? 'Cancel Request...' : 'Cancel Request'}
        </LoadingButton>
      </Stack>
      {!isLoading && endpointResponse && evaluationResults && (
        <TestEvaluationResultsStatusCheckIntegration
          endpointResponse={endpointResponse}
          evaluationResults={evaluationResults}
        />
      )}
    </Stack>
  )
}
